import { z } from 'zod';
import { CALENDAR_COLOR_HEX_REGEX } from '../constants/ageGroupCalendarColors';

const calendarColorSchema = z
  .string()
  .regex(CALENDAR_COLOR_HEX_REGEX, 'Calendar colour must be a valid hex colour (e.g. #22c55e)');

export const ageGroupFormSchema = z.object({
  label: z.string().min(1, 'Label is required').max(100, 'Label must be 100 characters or less'),
  method: z.enum(['age_range', 'ball_color', 'custom']),
  minAge: z.number().int().min(0).max(100).optional().nullable(),
  maxAge: z.number().int().min(0).max(100).optional().nullable(),
  calendarColor: calendarColorSchema,
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
}).superRefine((data, ctx) => {
  if (data.method === 'age_range') {
    if (data.minAge == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Min age is required for age range groups', path: ['minAge'] });
    }
    if (data.maxAge == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Max age is required for age range groups', path: ['maxAge'] });
    }
    if (data.minAge != null && data.maxAge != null && data.minAge > data.maxAge) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Min age must be less than or equal to max age', path: ['minAge'] });
    }
  }
});

export type AgeGroupFormData = z.infer<typeof ageGroupFormSchema>;

/** Body for PUT /age-groups/reorder — full list of ids in display order (sortOrder = index). */
export const ageGroupReorderSchema = z
  .object({
    academyId: z.string().uuid(),
    orderedIds: z.array(z.string().uuid()).min(1).max(100),
  })
  .superRefine((data, ctx) => {
    const unique = new Set(data.orderedIds);
    if (unique.size !== data.orderedIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Each age group id must appear exactly once',
        path: ['orderedIds'],
      });
    }
  });

export type AgeGroupReorderPayload = z.infer<typeof ageGroupReorderSchema>;
